import { ChangeEventHandler, FC, useMemo } from 'react';

import { isNonNullish, isNullish } from 'remeda';
import useSWR from 'swr';

import TextField from '@mui/material/TextField';

import { ticketService } from 'v2/api/ticketService';
import { Select } from 'v2/components/select/Select';
import { PromoCodeType, TicketType } from 'v2/constants';
import { SelectOption } from 'v2/types';
import { enumToSelect } from 'v2/utils';

interface PromoCodeRewardPickerProps {
  type: PromoCodeType;
  onTypeChange: (value: PromoCodeType) => void;
  value: string;
  onValueChange: (value: string) => void;
}

export const PromoCodeRewardPicker: FC<PromoCodeRewardPickerProps> = ({ type, onTypeChange, value, onValueChange }) => {
  const { data } = useSWR(
    (type === PromoCodeType.CONTEST_TICKET || type === PromoCodeType.GOLDEN_TICKET) && 'getTickets',
    ticketService.getTickets
  );

  const filteredData = useMemo(() => {
    if (isNullish(data)) return [];

    if (type === PromoCodeType.CONTEST_TICKET) {
      return data.filter((ticket) => ticket.type === TicketType.CONTEST);
    }

    if (type === PromoCodeType.GOLDEN_TICKET) {
      return data.filter((ticket) => ticket.type === TicketType.GOLDEN);
    }

    return data;
  }, [data, type]);

  const selectOptions: SelectOption[] = useMemo(() => {
    if (isNullish(filteredData)) return [];

    return filteredData.map((ticket) => ({ id: ticket.id, label: ticket.title, value: ticket.id }));
  }, [filteredData]);

  const handleTextFieldChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    onValueChange(e.target.value);
  };

  return (
    <div className="form-group">
      <Select value={type} options={enumToSelect(PromoCodeType)} label="Reward Type" onChange={onTypeChange} required />

      {isNonNullish(data) ? (
        <Select value={value} options={selectOptions} label="Reward Value" onChange={onValueChange} required />
      ) : (
        <TextField
          name="value"
          value={value}
          onChange={handleTextFieldChange}
          label="Reward Value"
          required
          fullWidth
        />
      )}
    </div>
  );
};
